import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@store/store';
import {
  ContactChannel,
  confirmContactVerification,
  requestContactVerification,
  resetContactVerification,
  selectContactVerification,
} from '@store/slices/userSlice';

export type ContactVerificationRequest = {
  channel: ContactChannel;
  value: string;
};

export const useContactVerification = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectContactVerification);

  const requestVerification = useCallback(
    (payload: ContactVerificationRequest) =>
      dispatch(requestContactVerification(payload)).unwrap(),
    [dispatch],
  );

  const confirmVerification = useCallback(
    (code: string) => {
      if (!state.channel) {
        return Promise.reject(new Error('No hay un canal pendiente para confirmar.'));
      }
      return dispatch(confirmContactVerification({ channel: state.channel, code })).unwrap();
    },
    [dispatch, state.channel],
  );

  const cancelVerification = useCallback(() => {
    dispatch(resetContactVerification());
  }, [dispatch]);

  const isModalOpen = useMemo(
    () =>
      ['awaiting', 'verifying'].includes(state.status) ||
      (state.status === 'error' && Boolean(state.expiresAt || state.maskedTarget)),
    [state.expiresAt, state.maskedTarget, state.status],
  );

  return {
    state,
    isModalOpen,
    requestVerification,
    confirmVerification,
    cancelVerification,
  };
};
